import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  TextField,
  Paper,
  Fab,
  Grid,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import GroupsIcon from "@mui/icons-material/Groups";
import SecurityIcon from "@mui/icons-material/Security";
import { useNavigate } from "react-router-dom";

// Particle Component for Hero Section
const ParticleBackground = () => (
  <div className="particles-container">
    {[...Array(12)].map((_, i) => (
      <span key={i} className="particle" />
    ))}
  </div>
);

// Glowing Orbs Component
const GlowingOrbs = () => (
  <>
    <div className="glow-orb glow-orb-1" />
    <div className="glow-orb glow-orb-2" />
    <div className="glow-orb glow-orb-3" />
  </>
);

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay }) => {
  const theme = useTheme();
  return (
    <Box
      className="float-animation shine-effect"
      sx={{
        p: "2rem",
        borderRadius: "1rem",
        backgroundColor: theme.palette.mode === "dark" 
          ? "rgba(30, 41, 59, 0.7)" 
          : "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${theme.palette.mode === "dark" 
          ? "rgba(255, 255, 255, 0.1)" 
          : "rgba(0, 0, 0, 0.08)"}`,
        boxShadow: theme.palette.mode === "dark"
          ? "0 8px 32px rgba(0, 0, 0, 0.3)"
          : "0 8px 32px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s ease",
        animationDelay: `${delay}s`,
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: theme.palette.mode === "dark"
            ? "0 16px 48px rgba(249, 115, 22, 0.2)"
            : "0 16px 48px rgba(249, 115, 22, 0.15)",
          borderColor: "rgba(249, 115, 22, 0.3)",
        },
      }}
    >
      <Box
        sx={{
          width: "60px",
          height: "60px",
          borderRadius: "12px",
          background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: "1.5rem",
          boxShadow: "0 8px 24px rgba(249, 115, 22, 0.3)",
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h5"
        fontWeight="600"
        sx={{ color: theme.palette.text.primary, mb: "0.75rem" }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const aboutRef = useRef(null);
  const contactRef = useRef(null);
  const featuresRef = useRef(null);

  // Scroll To Top Button State
  const [showScrollTop, setShowScrollTop] = useState(false);
  const pageScrollRef = useRef(null);

  // Contact form fields
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");

  // Contact form validation errors
  const [emailError, setEmailError] = useState("");
  const [contactResponse, setContactResponse] = useState("");
  const [contactLoading, setContactLoading] = useState(false);

  // Smooth scroll helper
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  // =============================
  // Email validation helper
  // =============================
  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required.";
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!pattern.test(email)) return "Please enter a valid email.";
    return "";
  };

  // =============================
  // Contact Form Submit (saves to backend, notifies admin in-app)
  // =============================
  const handleContactSubmit = async () => {
    setContactResponse("");

    const emailErr = validateEmail(contactEmail);
    setEmailError(emailErr);

    if (!contactName || !contactMessage || emailErr) {
      setContactResponse("⚠️ Please complete all required fields.");
      return;
    }

    setContactLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5001";
      const res = await fetch(`${backendUrl}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: contactName,
          email: contactEmail,
          message: contactMessage,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setContactResponse("❤️ Thank you for contacting us!");
      setContactName("");
      setContactEmail("");
      setContactMessage("");
    } catch (err) {
      setContactResponse(err.message || "Something went wrong.");
    } finally {
      setContactLoading(false);
    }
  };

  // =============================
  // Show Scroll-to-Top Button
  // =============================
  useEffect(() => {
    const el = pageScrollRef.current;
    if (!el) return undefined;
    const handleScroll = () => {
      setShowScrollTop(el.scrollTop > 400);
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    pageScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box
      ref={pageScrollRef}
      className="scrollbar-page"
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: theme.palette.background.default,
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {/* ================= NAVBAR ================= */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={{ xs: "1.5rem", md: "3rem" }}
        py="1rem"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: theme.palette.mode === "dark"
            ? "rgba(15, 23, 42, 0.85)"
            : "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.08)"
            : "rgba(0, 0, 0, 0.08)"}`,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box display="flex" alignItems="center" gap="0.75rem">
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
            }}
          >
            <DashboardIcon sx={{ color: "#fff", fontSize: "1.3rem" }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{ 
              color: theme.palette.text.primary,
              background: theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #fff 0%, #e2e8f0 100%)"
                : "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Endurova
          </Typography>
        </Box>

        <Box display="flex" gap={{ xs: "1rem", md: "2rem" }} alignItems="center">
          <Typography
            variant="body1"
            sx={{ 
              color: theme.palette.text.secondary, 
              cursor: "pointer",
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": { color: theme.palette.secondary.main },
              display: { xs: "none", sm: "block" },
            }}
            onClick={() => scrollToSection(featuresRef)}
          >
            Features
          </Typography>

          <Typography
            variant="body1"
            sx={{ 
              color: theme.palette.text.secondary, 
              cursor: "pointer",
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": { color: theme.palette.secondary.main },
              display: { xs: "none", sm: "block" },
            }}
            onClick={() => scrollToSection(aboutRef)}
          >
            About
          </Typography>

          <Typography
            variant="body1"
            sx={{ 
              color: theme.palette.text.secondary, 
              cursor: "pointer",
              fontWeight: 500,
              transition: "color 0.2s ease",
              "&:hover": { color: theme.palette.secondary.main },
              display: { xs: "none", sm: "block" },
            }}
            onClick={() => scrollToSection(contactRef)}
          >
            Contact
          </Typography>

          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              px: "1.5rem",
              py: "0.5rem",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              boxShadow: "0 4px 14px rgba(249, 115, 22, 0.35)",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
                boxShadow: "0 6px 20px rgba(249, 115, 22, 0.45)",
              },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      {/* ================= HERO SECTION ================= */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)"
            : "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)",
        }}
      >
        {/* Particle Effects */}
        <ParticleBackground />
        
        {/* Glowing Orbs */}
        <GlowingOrbs />

        {/* Grid Pattern Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: theme.palette.mode === "dark"
              ? `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)`
              : `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
            zIndex: 0,
          }}
        />

        {/* Hero Content */}
        <Box
          className="hero-content"
          display="flex"
          flexDirection="column"
          alignItems="center"
          textAlign="center"
          py="4rem"
          px="2rem"
          sx={{ maxWidth: "1100px", mx: "auto" }}
        >
          {/* Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              px: "1rem",
              py: "0.5rem",
              mb: "2rem",
              borderRadius: "50px",
              backgroundColor: theme.palette.mode === "dark"
                ? "rgba(249, 115, 22, 0.15)"
                : "rgba(249, 115, 22, 0.1)",
              border: "1px solid rgba(249, 115, 22, 0.3)",
            }}
          >
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "#f97316",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ 
                color: "#f97316", 
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              Trusted by Teams Worldwide
            </Typography>
          </Box>

          <Typography
            variant="h1"
            fontWeight="800"
            sx={{
              color: theme.palette.text.primary,
              maxWidth: "900px",
              mb: "1.5rem",
              fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
            }}
          >
            Smarter Operations Through{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fbbf24 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Intelligent Dashboards
            </Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mb: "3rem",
              lineHeight: 1.8,
              fontWeight: 400,
              fontSize: { xs: "1rem", md: "1.15rem" },
            }}
          >
            Endurova Team helps companies streamline workflow, automate tasks,
            visualize performance, and manage operations — all in one unified platform.
          </Typography>

          <Box display="flex" gap="1rem" flexWrap="wrap" justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: "2.5rem",
                py: "1rem",
                fontSize: "1.1rem",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                boxShadow: "0 8px 30px rgba(249, 115, 22, 0.4)",
                fontWeight: 600,
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
                  boxShadow: "0 12px 40px rgba(249, 115, 22, 0.5)",
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => navigate("/login")}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: "2.5rem",
                py: "1rem",
                fontSize: "1.1rem",
                borderRadius: "12px",
                borderColor: theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.2)",
                color: theme.palette.text.primary,
                fontWeight: 600,
                textTransform: "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#f97316",
                  backgroundColor: "rgba(249, 115, 22, 0.08)",
                  transform: "translateY(-2px)",
                },
              }}
              onClick={() => scrollToSection(featuresRef)}
            >
              Explore Features
            </Button>
          </Box>

          {/* Stats */}
          <Box
            display="flex"
            gap={{ xs: "2rem", md: "4rem" }}
            mt="4rem"
            flexWrap="wrap"
            justifyContent="center"
          >
            {[
              { value: "99.9%", label: "Uptime" },
              { value: "500+", label: "Teams" },
              { value: "24/7", label: "Support" },
            ].map((stat, index) => (
              <Box key={index} textAlign="center">
                <Typography
                  variant="h3"
                  fontWeight="700"
                  sx={{
                    background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: "0.25rem",
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ================= FEATURES SECTION ================= */}
      <Box
        ref={featuresRef}
        py="6rem"
        px="2rem"
        sx={{ 
          backgroundColor: theme.palette.background.default,
          position: "relative",
        }}
      >
        <Typography
          variant="h3"
          fontWeight="700"
          textAlign="center"
          sx={{ 
            color: theme.palette.text.primary, 
            mb: "1rem",
          }}
        >
          Powerful Features
        </Typography>
        <Typography
          variant="body1"
          textAlign="center"
          sx={{ 
            color: theme.palette.text.secondary, 
            mb: "4rem",
            maxWidth: "600px",
            mx: "auto",
          }}
        >
          Everything you need to manage your team and operations efficiently
        </Typography>

        <Grid container spacing={3} maxWidth="1200px" mx="auto">
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              icon={<DashboardIcon sx={{ color: "#fff", fontSize: "1.75rem" }} />}
              title="Smart Dashboard"
              description="Real-time analytics and insights at your fingertips with customizable widgets."
              delay={0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              icon={<AutoGraphIcon sx={{ color: "#fff", fontSize: "1.75rem" }} />}
              title="Performance Tracking"
              description="Monitor team performance with detailed metrics and visual reports."
              delay={0.1}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              icon={<GroupsIcon sx={{ color: "#fff", fontSize: "1.75rem" }} />}
              title="Team Management"
              description="Organize teams, assign tasks, and streamline collaboration effortlessly."
              delay={0.2}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard
              icon={<SecurityIcon sx={{ color: "#fff", fontSize: "1.75rem" }} />}
              title="Secure & Reliable"
              description="Enterprise-grade security with 99.9% uptime guarantee for your data."
              delay={0.3}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ================= ABOUT SECTION ================= */}
      <Box
        ref={aboutRef}
        py="6rem"
        px="2rem"
        sx={{ 
          backgroundColor: theme.palette.background.alt,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative gradient blob */}
        <Box
          sx={{
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(249, 115, 22, 0.08) 0%, transparent 70%)",
            top: "-200px",
            right: "-200px",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />

        <Box maxWidth="1000px" mx="auto" position="relative">
          <Typography
            variant="h3"
            fontWeight="700"
            textAlign="center"
            sx={{ color: theme.palette.text.primary, mb: "1rem" }}
          >
            About Us
          </Typography>
          
          <Typography
            variant="body1"
            textAlign="center"
            sx={{ 
              color: theme.palette.text.secondary, 
              mb: "4rem",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Building the future of team operations
          </Typography>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: "2.5rem",
                  height: "100%",
                  borderRadius: "1.5rem",
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)"}`,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{ 
                    color: theme.palette.text.primary, 
                    mb: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <Box
                    sx={{
                      width: "4px",
                      height: "24px",
                      borderRadius: "2px",
                      background: "linear-gradient(180deg, #f97316 0%, #fb923c 100%)",
                    }}
                  />
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}
                >
                  Endurova Team delivers operational visibility and workflow automation for modern
                  teams. Whether you're tracking performance, managing tasks, scheduling workflows,
                  or analyzing strategy — Endurova brings everything together in one unified place.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: "2.5rem",
                  height: "100%",
                  borderRadius: "1.5rem",
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)"}`,
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{ 
                    color: theme.palette.text.primary, 
                    mb: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <Box
                    sx={{
                      width: "4px",
                      height: "24px",
                      borderRadius: "2px",
                      background: "linear-gradient(180deg, #3b82f6 0%, #60a5fa 100%)",
                    }}
                  />
                  Our Vision
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}
                >
                  Built with scalability and simplicity in mind, Endurova empowers organizations of
                  all sizes to manage people, processes, and data effortlessly. We believe in
                  creating tools that adapt to your workflow, not the other way around.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ================= CONTACT SECTION ================= */}
      <Box
        ref={contactRef}
        py="6rem"
        px="2rem"
        sx={{ 
          backgroundColor: theme.palette.background.default,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative gradient blob */}
        <Box
          sx={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)",
            bottom: "-150px",
            left: "-150px",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />

        <Typography
          variant="h3"
          fontWeight="700"
          textAlign="center"
          sx={{ color: theme.palette.text.primary, mb: "1rem" }}
        >
          Get In Touch
        </Typography>

        <Typography
          variant="body1"
          textAlign="center"
          sx={{ 
            color: theme.palette.text.secondary, 
            mb: "3rem",
            maxWidth: "500px",
            mx: "auto",
          }}
        >
          Have questions? We'd love to hear from you.
        </Typography>

        <Paper
          sx={{
            maxWidth: "550px",
            mx: "auto",
            p: { xs: "1.5rem", md: "2.5rem" },
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(30, 41, 59, 0.6)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.06)"}`,
            boxShadow: "0 16px 48px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
        >
          <TextField
            label="Full Name"
            fullWidth
            sx={{ 
              mb: "1.25rem",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />

          <TextField
            label="Email"
            fullWidth
            sx={{ 
              mb: "1.25rem",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              setEmailError(validateEmail(e.target.value));
            }}
            error={Boolean(emailError)}
            helperText={emailError}
          />

          <TextField
            label="Your Message"
            fullWidth
            multiline
            rows={4}
            sx={{ 
              mb: "1.5rem",
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            value={contactMessage}
            onChange={(e) => setContactMessage(e.target.value)}
          />

          <Button
            variant="contained"
            fullWidth
            sx={{ 
              py: "1rem", 
              fontSize: "1rem",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
              boxShadow: "0 6px 20px rgba(249, 115, 22, 0.35)",
              fontWeight: 600,
              textTransform: "none",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
                boxShadow: "0 8px 25px rgba(249, 115, 22, 0.45)",
                transform: "translateY(-2px)",
              },
            }}
            onClick={handleContactSubmit}
            disabled={contactLoading}
          >
            {contactLoading ? "Sending your message..." : contactResponse ? "Send Another Message" : "Send Message"}
          </Button>

          {contactResponse && (
            <Box
              sx={{
                mt: "1.25rem",
                p: "1rem",
                borderRadius: "10px",
                backgroundColor: contactResponse.includes("Thank") 
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(239, 68, 68, 0.1)",
                border: `1px solid ${contactResponse.includes("Thank")
                  ? "rgba(34, 197, 94, 0.3)"
                  : "rgba(239, 68, 68, 0.3)"}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  color: contactResponse.includes("Thank") ? "#22c55e" : "#ef4444",
                  fontWeight: 600,
                }}
              >
                {contactResponse}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ================= FOOTER ================= */}
      <Box
        py="3rem"
        px="2rem"
        sx={{
          background: theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)"
            : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)",
          borderTop: `1px solid ${theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.05)"}`,
        }}
      >
        <Box maxWidth="1200px" mx="auto">
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems="center"
            gap="1.5rem"
          >
            <Box display="flex" alignItems="center" gap="0.75rem">
              <Box
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
                }}
              >
                <DashboardIcon sx={{ color: "#fff", fontSize: "1.1rem" }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{ color: theme.palette.text.primary }}
              >
                Endurova Team
              </Typography>
            </Box>

            <Box display="flex" gap="2rem">
              {["Features", "About", "Contact"].map((item) => (
                <Typography
                  key={item}
                  variant="body2"
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                    "&:hover": { color: theme.palette.secondary.main },
                  }}
                  onClick={() => scrollToSection(
                    item === "Features" ? featuresRef : 
                    item === "About" ? aboutRef : contactRef
                  )}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            <Typography 
              variant="body2" 
              sx={{ color: theme.palette.text.secondary }}
            >
              © {new Date().getFullYear()} Endurova Team. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ================= BACK TO TOP BUTTON ================= */}
      {showScrollTop && (
        <Fab
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: "linear-gradient(135deg, #f97316 0%, #fb923c 100%)",
            boxShadow: "0 6px 20px rgba(249, 115, 22, 0.4)",
            transition: "all 0.3s ease",
            "&:hover": { 
              background: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
              boxShadow: "0 8px 25px rgba(249, 115, 22, 0.5)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <KeyboardArrowUpIcon sx={{ color: "#fff" }} />
        </Fab>
      )}
    </Box>
  );
};

export default Home;
